import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Calendar } from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const Informes = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedMes, setSelectedMes] = useState(new Date().toISOString().slice(0, 7));
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await api.get('/reportes/clientes-con-tareas');
      setClientes(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const handleGenerarInforme = async () => {
    if (!selectedClient) {
      toast.error('Selecciona un cliente');
      return;
    }

    setGeneratingPDF(true);
    try {
      const response = await api.post(
        `/reportes/generar-informe/${selectedClient}/${selectedMes}`,
        null,
        { responseType: 'blob' }
      );

      const cliente = clientes.find(c => c.id === selectedClient);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_${cliente?.name.replace(' ', '_')}_${selectedMes}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Informe mensual generado exitosamente');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-4xl font-bold text-slate-900" data-testid="informes-title">
                Informes Mensuales
              </h1>
            </div>
            <p className="text-lg text-slate-600">
              Generación de reportes de éxito del cliente
            </p>
          </div>

          {/* Generator Card */}
          <Card className="p-8 border-slate-200 max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-slate-900">
                Generar Informe Mensual
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 mb-2 block">Cliente</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company ? `- ${client.company}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700 mb-2 block">Mes</Label>
                <Input
                  type="month"
                  value={selectedMes}
                  onChange={(e) => setSelectedMes(e.target.value)}
                  data-testid="month-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>El informe incluirá:</strong><br />
                  • Resumen ejecutivo del período<br />
                  • Tareas completadas agrupadas por área<br />
                  • Estado de trámites (Legal, Tributaria, Contabilidad)<br />
                  • Tareas pendientes para el próximo mes
                </p>
              </div>

              <Button
                onClick={handleGenerarInforme}
                disabled={generatingPDF || !selectedClient}
                className="w-full bg-primary hover:bg-primary/90 py-6 text-lg font-medium"
                data-testid="generate-report-btn"
              >
                {generatingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Generando PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Generar Informe PDF
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 max-w-2xl mx-auto">
            <Card className="p-6 border-slate-200">
              <div className="text-center">
                <FileText className="w-12 h-12 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Automatizado
                </h3>
                <p className="text-sm text-slate-600">
                  El sistema agrupa automáticamente todas las tareas del mes
                </p>
              </div>
            </Card>

            <Card className="p-6 border-slate-200">
              <div className="text-center">
                <Calendar className="w-12 h-12 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Mensual
                </h3>
                <p className="text-sm text-slate-600">
                  Selecciona el mes para generar el informe correspondiente
                </p>
              </div>
            </Card>

            <Card className="p-6 border-slate-200">
              <div className="text-center">
                <Download className="w-12 h-12 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  PDF Profesional
                </h3>
                <p className="text-sm text-slate-600">
                  Formato listo para enviar directamente al cliente
                </p>
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Informes;
